import React, { useState, useEffect, useRef } from 'react';
import { setPageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicScheduling = ({ businessId }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addonProducts, setAddonProducts] = useState([]);
  const [orderForms, setOrderForms] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [mileageFee, setMileageFee] = useState(0);
  const [availability, setAvailability] = useState({ slots: [] });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [fromClientPortal, setFromClientPortal] = useState(false);
  const [isReschedule, setIsReschedule] = useState(false);
  const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [quoteSlotIndex, setQuoteSlotIndex] = useState(null);
  
  // Client authentication state
  const [clientLoggedIn, setClientLoggedIn] = useState(false);
  const [clientToken, setClientToken] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '', phone: '' });
  
  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const addressInputRef = useRef(null);
  
  // Check for existing client session or main platform session
  useEffect(() => {
    const clientToken = localStorage.getItem('clientToken');
    const mainToken = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (clientToken) {
      // Verify client token and get client info
      verifyClientToken(clientToken);
    } else if (mainToken) {
      // Immediately load cached user data for instant form fill
      if (userData) {
        try {
          const cached = JSON.parse(userData);
          if (cached.email || cached.user_email) {
            setClientLoggedIn(true);
            setClientToken(mainToken);
            setFormData(prev => ({
              ...prev,
              client_name: cached.user_name || cached.full_name || prev.client_name,
              client_email: cached.email || cached.user_email || prev.client_email,
              client_phone: cached.phone || prev.client_phone,
            }));
          }
        } catch (e) {}
      }
      // Also verify with API to get latest info
      verifyMainToken(mainToken);
    }
  }, []);
  
  const verifyMainToken = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/me/role`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const user = await res.json();
        // Only auto-fill if user has client-like info (name, email)
        if (user.email) {
          setClientLoggedIn(true);
          setClientToken(token);
          setFromClientPortal(false);
          // Pre-fill form data with user info
          setFormData(prev => ({
            ...prev,
            client_name: user.full_name || prev.client_name,
            client_email: user.email || prev.client_email,
            client_phone: user.phone || prev.client_phone,
          }));
        }
      }
    } catch (err) {
      console.error('Error verifying main token:', err);
    }
  };
  
  const verifyClientToken = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/client-portal/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const profile = await res.json();
        setClientLoggedIn(true);
        setClientToken(token);
        setFromClientPortal(true);
        // Pre-fill form data with client info
        setFormData(prev => ({
          ...prev,
          client_name: profile.name || profile.full_name || prev.client_name,
          client_email: profile.email || prev.client_email,
          client_phone: profile.phone || prev.client_phone,
          client_company: profile.company || prev.client_company
        }));
      } else {
        localStorage.removeItem('clientToken');
      }
    } catch (err) {
      console.error('Error verifying client token:', err);
      localStorage.removeItem('clientToken');
    }
  };
  
  const handleClientLogin = async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email, password: authForm.password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      
      if (data.role !== 'client') {
        throw new Error('Please use client credentials. Business accounts should use the main login.');
      }
      
      localStorage.setItem('clientToken', data.access_token);
      setClientToken(data.access_token);
      setClientLoggedIn(true);
      setFromClientPortal(true);
      setShowAuthModal(false);
      
      // Fetch and pre-fill client profile
      await verifyClientToken(data.access_token);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };
  
  const handleClientRegister = async () => {
    setAuthLoading(true);
    setAuthError('');
    try {
      // Register new client
      const registerRes = await fetch(`${API_URL}/api/client-portal/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authForm.email,
          password: authForm.password,
          name: authForm.name || formData.client_name,
          phone: authForm.phone || formData.client_phone
        })
      });
      const registerData = await registerRes.json();
      if (!registerRes.ok) throw new Error(registerData.detail || 'Registration failed');
      
      // Login after registration
      const loginRes = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authForm.email, password: authForm.password })
      });
      const loginData = await loginRes.json();
      if (!loginRes.ok) throw new Error(loginData.detail || 'Login failed');
      
      localStorage.setItem('clientToken', loginData.access_token);
      setClientToken(loginData.access_token);
      setClientLoggedIn(true);
      setFromClientPortal(true);
      setShowAuthModal(false);
      
      // Update form data with registered info
      setFormData(prev => ({
        ...prev,
        client_name: authForm.name || prev.client_name,
        client_email: authForm.email,
        client_phone: authForm.phone || prev.client_phone
      }));
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Check for pre-filled client data from client portal or reschedule data
  const getInitialFormData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for quote mode
    const quoteId = urlParams.get('quote');
    const slotIdx = urlParams.get('slot');
    
    if (quoteId) {
      // Quote data will be loaded asynchronously; return empty for now
      setQuoteSlotIndex(slotIdx !== null ? parseInt(slotIdx) : null);
      return {
        property_address: '',
        property_sqft: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        client_company: '',
        special_instructions: '',
        selected_products: [],
        selected_addons: [],
        selected_package: null,
        photographer_id: '',
        scheduled_date: '',
        scheduled_time: '',
        custom_form_answers: {}
      };
    }
    
    // Check for reschedule mode
    const rescheduleId = urlParams.get('reschedule');
    
    if (rescheduleId) {
      const rescheduleData = sessionStorage.getItem('rescheduleAppointment');
      if (rescheduleData) {
        try {
          const parsed = JSON.parse(rescheduleData);
          setIsReschedule(true);
          setRescheduleAppointmentId(rescheduleId);
          setFromClientPortal(true);
          return {
            property_address: parsed.property_address || '',
            property_sqft: parsed.property_sqft || '',
            client_name: parsed.client_name || '',
            client_email: parsed.client_email || '',
            client_phone: parsed.client_phone || '',
            client_company: '',
            special_instructions: parsed.special_instructions || '',
            selected_products: parsed.selected_products || [],
            selected_addons: parsed.selected_addons || [],
            selected_package: null,
            photographer_id: parsed.photographer_id || '',
            scheduled_date: '',
            scheduled_time: '',
            custom_form_answers: {}
          };
        } catch (e) {
          console.error('Error parsing reschedule data:', e);
        }
      }
    }
    
    // Check for new booking from client portal
    const clientData = sessionStorage.getItem('clientPortalBooking');
    if (clientData) {
      try {
        const parsed = JSON.parse(clientData);
        setFromClientPortal(true);
        return {
          property_address: '',
          property_sqft: '',
          client_name: parsed.client_name || '',
          client_email: parsed.client_email || '',
          client_phone: parsed.client_phone || '',
          client_company: parsed.client_company || '',
          special_instructions: '',
          selected_products: [],
          selected_addons: [],
          selected_package: null,
          photographer_id: '',
          scheduled_date: '',
          scheduled_time: '',
          custom_form_answers: {}
        };
      } catch (e) {
        console.error('Error parsing client data:', e);
      }
    }
    return {
      property_address: '',
      property_sqft: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      client_company: '',
      special_instructions: '',
      selected_products: [],
      selected_addons: [],
      selected_package: null,
      photographer_id: '',
      scheduled_date: '',
      scheduled_time: '',
      custom_form_answers: {}
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  // Load quote data if quote param is present
  // Wait until products are loaded so getTotalDuration works correctly
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const quoteId = urlParams.get('quote');
    if (!quoteId) return;
    // Don't load until products are fetched (needed for duration calculation)
    if (products.length === 0) return;
    // Don't re-run if already loaded
    if (quoteData) return;
    
    const loadQuote = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/quotes/${quoteId}`);
        if (!res.ok) return;
        const quote = await res.json();
        if (quote.error) {
          setError(quote.error);
          return;
        }
        
        setQuoteData(quote);
        
        // Determine the selected slot
        const slotIdx = quoteSlotIndex !== null ? quoteSlotIndex : 0;
        const selectedSlot = quote.time_slots?.[slotIdx] || {};
        
        // Map photographer_id "any" to empty string (meaning "Any Photographer")
        const photographerId = selectedSlot.photographer_id === 'any' ? '' : (selectedSlot.photographer_id || '');
        const slotDate = selectedSlot.date || '';
        const slotTime = selectedSlot.time || '';
        
        // Set all form data from the quote
        setFormData(prev => ({
          ...prev,
          property_address: quote.property_address || '',
          property_sqft: quote.property_sqft ? String(quote.property_sqft) : '',
          client_name: quote.client_name || '',
          client_email: quote.client_email || '',
          client_phone: quote.client_phone || '',
          special_instructions: quote.notes || '',
          selected_products: quote.selected_products || [],
          photographer_id: photographerId,
          scheduled_date: slotDate,
          scheduled_time: slotTime,
        }));
        
        // Navigate calendar to the slot's month
        if (slotDate) {
          const [year, month] = slotDate.split('-');
          setCurrentMonth(new Date(parseInt(year), parseInt(month) - 1));
        }
        
        setFromClientPortal(true);
        // Jump to scheduling step with pre-filled date/time
        setStep(4);
      } catch (e) {
        console.error('Error loading quote:', e);
      }
    };
    loadQuote();
  }, [quoteSlotIndex, products, quoteData]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setAutocompleteService(new window.google.maps.places.AutocompleteService());
      }
    };
    if (window.google && window.google.maps) {
      initAutocomplete();
    } else {
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          initAutocomplete();
          clearInterval(checkInterval);
        }
      }, 100);
      return () => clearInterval(checkInterval);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressInputRef.current && !addressInputRef.current.contains(event.target)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchBusinessInfo();
  }, [businessId]);

  // Reflect the active business in the browser title so customers who land on
  // a booking page see the company name (e.g. "Book a shoot with HomePix Media")
  // instead of the generic site title.
  useEffect(() => {
    if (business?.business_name) {
      setPageTitle(`Book with ${business.business_name}`);
    }
    return () => setPageTitle();
  }, [business?.business_name]);

  // Set step to 4 (scheduling) when in reschedule mode
  useEffect(() => {
    if (isReschedule) {
      setStep(4);
    }
  }, [isReschedule]);

  // Fetch photographers when moving to step 4 (scheduling), filtered by selected products
  useEffect(() => {
    if (step === 4) {
      fetchPhotographers();
    }
  }, [step, formData.selected_products, formData.selected_package]);

  // Fetch availability when date or photographer changes
  useEffect(() => {
    if (formData.scheduled_date) {
      fetchAvailability();
    }
  }, [formData.scheduled_date, formData.photographer_id]);

  // Retry helper for API calls
  const fetchWithRetry = async (url, maxRetries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const data = await res.json();
        return { success: true, data };
      } catch (err) {
        console.warn(`Attempt ${attempt}/${maxRetries} failed for ${url}:`, err.message);
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }
    return { success: false, data: null };
  };

  const fetchBusinessInfo = async () => {
    try {
      const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}`);
      if (!result.success || !result.data) {
        throw new Error('Business not found');
      }
      setBusiness(result.data);
      
      // Fetch all data in parallel with retry logic
      const [productsRes, packagesRes, addonsRes, formsRes] = await Promise.all([
        fetchWithRetry(`${API_URL}/api/public/business/${businessId}/products`),
        fetchWithRetry(`${API_URL}/api/public/business/${businessId}/packages`),
        fetchWithRetry(`${API_URL}/api/public/business/${businessId}/addon-products`),
        fetchWithRetry(`${API_URL}/api/public/business/${businessId}/order-forms`)
      ]);
      
      // Set data with validation
      if (productsRes.success && Array.isArray(productsRes.data)) {
        setProducts(productsRes.data);
      } else {
        console.error('Products fetch failed or invalid data');
        setProducts([]);
      }
      
      if (packagesRes.success && Array.isArray(packagesRes.data)) {
        setPackages(packagesRes.data);
      } else {
        console.error('Packages fetch failed or invalid data');
        setPackages([]);
      }
      
      if (addonsRes.success && Array.isArray(addonsRes.data)) {
        setAddonProducts(addonsRes.data);
      } else {
        console.error('Addon products fetch failed or invalid data');
        setAddonProducts([]);
      }
      
      if (formsRes.success && Array.isArray(formsRes.data)) {
        setOrderForms(formsRes.data);
      } else {
        console.error('Order forms fetch failed or invalid data');
        setOrderForms([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('fetchBusinessInfo error:', err);
      setError('Unable to load booking page. Please check the URL and try again.');
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}/products`);
    if (result.success && Array.isArray(result.data)) {
      setProducts(result.data);
    } else {
      console.error('Failed to fetch products after retries');
    }
  };

  const fetchPackages = async () => {
    const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}/packages`);
    if (result.success && Array.isArray(result.data)) {
      setPackages(result.data);
    } else {
      console.error('Failed to fetch packages after retries');
    }
  };

  const fetchAddonProducts = async () => {
    const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}/addon-products`);
    if (result.success && Array.isArray(result.data)) {
      setAddonProducts(result.data);
    } else {
      console.error('Failed to fetch addon products after retries');
    }
  };

  const fetchOrderForms = async () => {
    const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}/order-forms`);
    if (result.success && Array.isArray(result.data)) {
      setOrderForms(result.data);
    } else {
      console.error('Failed to fetch order forms after retries');
    }
  };

  const fetchPhotographers = async () => {
    try {
      const productIds = formData.selected_package 
        ? packages.find(p => p.id === formData.selected_package)?.product_ids?.join(',') || ''
        : formData.selected_products.join(',');
      
      // Include property address for territory filtering
      const params = new URLSearchParams();
      if (productIds) params.append('product_ids', productIds);
      if (formData.property_address) params.append('property_address', formData.property_address);
      
      const result = await fetchWithRetry(`${API_URL}/api/public/business/${businessId}/photographers?${params.toString()}`);
      if (result.success && Array.isArray(result.data)) {
        setPhotographers(result.data);
      } else {
        console.error('Failed to fetch photographers after retries');
        setPhotographers([]);
      }
      
      // Reset mileage fee when photographers change
      setMileageFee(0);
    } catch (err) {
      console.error('Failed to fetch photographers:', err);
      setPhotographers([]);
    }
  };

  const fetchAvailability = async () => {
    setLoadingSlots(true);
    setAvailability({ slots: [] }); // Clear previous slots while loading
    try {
      // Calculate total duration from selected products/package
      const totalDuration = getTotalDuration();
      console.log('Fetching availability with duration:', totalDuration);
      
      // Build query params including property address for drive time calculation
      const params = new URLSearchParams();
      params.append('date', formData.scheduled_date);
      params.append('photographer_id', formData.photographer_id);
      params.append('duration_minutes', totalDuration || 60);
      if (formData.property_address) {
        params.append('property_address', formData.property_address);
      }
      
      const result = await fetchWithRetry(
        `${API_URL}/api/public/business/${businessId}/availability?${params.toString()}`
      );
      
      if (result.success && result.data) {
        console.log('Availability response:', result.data);
        setAvailability(result.data);
      } else {
        console.error('Failed to fetch availability after retries');
        setAvailability({ slots: [], error: 'Failed to load times. Please try again.' });
      }
    } catch (err) {
      console.error('Failed to fetch availability:', err);
      setAvailability({ slots: [], error: 'Failed to load times' });
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleAddressChange = (value) => {
    setFormData({ ...formData, property_address: value });
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    if (!autocompleteService) return;
    
    autocompleteService.getPlacePredictions(
      { input: value, types: ['address'], componentRestrictions: { country: 'us' } },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setAddressSuggestions(predictions);
          setShowAddressSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
        }
      }
    );
  };

  const selectAddressSuggestion = (suggestion) => {
    setFormData({ ...formData, property_address: suggestion.description });
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Handle reschedule mode - only update date/time
      if (isReschedule && rescheduleAppointmentId) {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/client-portal/appointment/${rescheduleAppointmentId}/reschedule`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            scheduled_date: formData.scheduled_date,
            scheduled_time: formData.scheduled_time
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Reschedule failed');
        }
        const result = await res.json();
        setBookingResult(result);
        // Clean up reschedule data from session storage
        sessionStorage.removeItem('rescheduleAppointment');
        setSuccess(true);
        return;
      }
      
      // First, upload any files from custom form answers
      const processedAnswers = { ...formData.custom_form_answers };
      
      for (const [questionId, answer] of Object.entries(processedAnswers)) {
        // Check if the answer is a File object
        if (answer instanceof File) {
          try {
            const fileFormData = new FormData();
            fileFormData.append('file', answer);
            
            const uploadRes = await fetch(`${API_URL}/api/public/business/${businessId}/upload-form-file`, {
              method: 'POST',
              body: fileFormData
            });
            
            if (!uploadRes.ok) {
              const uploadErr = await uploadRes.json();
              throw new Error(uploadErr.detail || 'File upload failed');
            }
            
            const uploadResult = await uploadRes.json();
            // Replace File object with the uploaded file info
            processedAnswers[questionId] = {
              filename: uploadResult.original_filename,
              file_url: uploadResult.file_url,
              content_type: uploadResult.content_type
            };
          } catch (uploadErr) {
            setError(`Failed to upload file: ${uploadErr.message}`);
            setLoading(false);
            return;
          }
        }
      }
      
      const res = await fetch(`${API_URL}/api/public/business/${businessId}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          property_sqft: formData.property_sqft ? parseInt(formData.property_sqft) : null,
          custom_form_answers: processedAnswers,
          mileage_fee: mileageFee
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Booking failed');
      }
      const result = await res.json();
      setBookingResult(result);
      
      // If this was a quote booking, mark the quote as accepted
      if (quoteData?.id) {
        try {
          const slotIdx = quoteSlotIndex !== null ? quoteSlotIndex : 0;
          await fetch(`${API_URL}/api/public/quotes/${quoteData.id}/accept`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slot_index: slotIdx })
          });
        } catch (quoteErr) {
          console.error('Failed to mark quote as accepted:', quoteErr);
        }
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get price based on sqft tiers
  const getProductPrice = (product) => {
    const sqft = parseInt(formData.property_sqft) || 0;
    const tiers = product.pricing_tiers || [];
    if (tiers.length > 0 && sqft > 0) {
      const matchingTier = tiers.find(t => sqft >= t.min_sqft && sqft <= t.max_sqft);
      if (matchingTier) return matchingTier.price;
      const highestTier = tiers.reduce((max, t) => t.max_sqft > max.max_sqft ? t : max, tiers[0]);
      if (sqft > highestTier.max_sqft) return highestTier.price;
    }
    return product.price || 0;
  };

  const getProductDuration = (product) => {
    const sqft = parseInt(formData.property_sqft) || 0;
    const tiers = product.pricing_tiers || [];
    if (tiers.length > 0 && sqft > 0) {
      const matchingTier = tiers.find(t => sqft >= t.min_sqft && sqft <= t.max_sqft);
      if (matchingTier && matchingTier.duration_minutes) return matchingTier.duration_minutes;
      const highestTier = tiers.reduce((max, t) => t.max_sqft > max.max_sqft ? t : max, tiers[0]);
      if (sqft > highestTier.max_sqft && highestTier.duration_minutes) return highestTier.duration_minutes;
    }
    return product.duration_minutes || product.duration || 0;
  };

  // Get package price (calculated from products with discount OR fixed price with tiers)
  const getPackagePrice = (pkg) => {
    const sqft = parseInt(formData.property_sqft) || 0;
    
    // Check if package has its own pricing tiers
    const tiers = pkg.pricing_tiers || [];
    if (tiers.length > 0 && sqft > 0) {
      const matchingTier = tiers.find(t => sqft >= t.min_sqft && sqft <= t.max_sqft);
      if (matchingTier) return matchingTier.price;
      const highestTier = tiers.reduce((max, t) => t.max_sqft > max.max_sqft ? t : max, tiers[0]);
      if (sqft > highestTier.max_sqft) return highestTier.price;
    }
    
    // If package has a fixed price, use it
    if (pkg.price) return pkg.price;
    
    // Calculate from products (with tiered pricing) and apply discount
    if (pkg.products && pkg.products.length > 0) {
      const productsTotal = pkg.products.reduce((sum, p) => sum + parseFloat(getProductPrice(p)), 0);
      const discount = pkg.discount_percentage || 0;
      return productsTotal * (1 - discount / 100);
    }
    
    return 0;
  };

  // Get package duration
  const getPackageDuration = (pkg) => {
    const sqft = parseInt(formData.property_sqft) || 0;
    
    // Check if package has its own pricing tiers with duration
    const tiers = pkg.pricing_tiers || [];
    if (tiers.length > 0 && sqft > 0) {
      const matchingTier = tiers.find(t => sqft >= t.min_sqft && sqft <= t.max_sqft);
      if (matchingTier && matchingTier.duration_minutes) return matchingTier.duration_minutes;
    }
    
    // If package has fixed duration, use it
    if (pkg.duration_minutes) return pkg.duration_minutes;
    
    // Calculate from products
    if (pkg.products && pkg.products.length > 0) {
      return pkg.products.reduce((sum, p) => sum + parseInt(getProductDuration(p)), 0);
    }
    
    return 0;
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      selected_package: null, // Deselect package when selecting individual products
      selected_products: prev.selected_products.includes(productId)
        ? prev.selected_products.filter(id => id !== productId)
        : [...prev.selected_products, productId]
    }));
  };

  const selectPackage = (packageId) => {
    setFormData(prev => ({
      ...prev,
      selected_products: [], // Deselect products when selecting package
      selected_package: prev.selected_package === packageId ? null : packageId
    }));
  };

  const getSelectedProductsTotal = () => {
    if (formData.selected_package) {
      const pkg = packages.find(p => p.id === formData.selected_package);
      return pkg ? getPackagePrice(pkg) : 0;
    }
    return products
      .filter(p => formData.selected_products.includes(p.id))
      .reduce((sum, p) => sum + parseFloat(getProductPrice(p)), 0);
  };

  const getAddonsTotal = () => {
    return addonProducts
      .filter(a => formData.selected_addons.includes(a.id))
      .reduce((sum, a) => sum + parseFloat(a.price || 0), 0);
  };

  const getGrandTotal = () => {
    return getSelectedProductsTotal() + getAddonsTotal() + mileageFee;
  };

  const calculateTotal = () => getGrandTotal();

  const getTotalDuration = () => {
    if (formData.selected_package) {
      const pkg = packages.find(p => p.id === formData.selected_package);
      return pkg ? getPackageDuration(pkg) : 0;
    }
    return products
      .filter(p => formData.selected_products.includes(p.id))
      .reduce((sum, p) => sum + parseInt(getProductDuration(p)), 0);
  };

  const getSelectedServiceNames = () => {
    if (formData.selected_package) {
      const pkg = packages.find(p => p.id === formData.selected_package);
      return pkg?.name || '';
    }
    return products.filter(p => formData.selected_products.includes(p.id)).map(p => p.name).join(', ');
  };

  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const isDateSelectable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + 1);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 60);
    return checkDate >= minDate && checkDate <= maxDate;
  };

  const handleDateSelect = (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const selectedDate = new Date(year, month, day);
    if (isDateSelectable(selectedDate)) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      setFormData({ ...formData, scheduled_date: dateStr, scheduled_time: '' });
    }
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateStr = date.toISOString().split('T')[0];
      const isSelected = formData.scheduled_date === dateStr;
      const isSelectable = isDateSelectable(date);
      const isToday = date.toDateString() === today.toDateString();
      
      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateSelect(day)}
          disabled={!isSelectable}
          className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium transition-all
            ${isSelected ? 'bg-blue-600 text-white' : ''}
            ${!isSelected && isSelectable ? 'hover:bg-blue-100 text-gray-700' : ''}
            ${!isSelectable ? 'text-gray-300 cursor-not-allowed' : ''}
            ${isToday && !isSelected ? 'ring-2 ring-blue-300' : ''}
          `}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  // Step 1: Services - need sqft to calculate prices
  const canProceedToStep2 = () => {
    return formData.property_sqft && (formData.selected_products.length > 0 || formData.selected_package);
  };

  // Step 2: Property Details - need address and contact info
  const canProceedToStep3 = () => {
    return formData.property_address && formData.client_name && formData.client_email;
  };

  const canSubmit = () => {
    return formData.scheduled_date && formData.scheduled_time;
  };

  if (loading && !business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    const isPendingReview = bookingResult?.status === 'pending_review';
    
    const handleReturnToPortal = () => {
      sessionStorage.removeItem('clientPortalBooking');
      sessionStorage.removeItem('rescheduleAppointment');
      window.location.href = '/';
    };

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center max-w-md w-full">
          <div className={`w-16 h-16 ${isPendingReview ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isPendingReview ? (
              <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {isPendingReview 
              ? 'Booking Request Submitted!' 
              : isReschedule 
                ? 'Appointment Rescheduled!' 
                : 'Booking Confirmed!'}
          </h2>
          <p className="text-gray-500 mb-6">
            {isPendingReview 
              ? 'Your booking request is pending review. We\'ll contact you shortly to confirm your appointment.'
              : isReschedule 
                ? 'Your appointment has been successfully rescheduled.' 
                : <>A confirmation email has been sent to <span className="font-medium text-gray-700">{formData.client_email}</span></>
            }
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {isPendingReview ? 'Request Details' : isReschedule ? 'New Appointment Details' : 'Booking Details'}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Property</span>
                <span className="text-gray-900 font-medium text-right max-w-[200px] truncate">{formData.property_address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-900 font-medium">
                  {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Time</span>
                <span className="text-gray-900 font-medium">{formatTime(formData.scheduled_time)}</span>
              </div>
              {!isReschedule && (
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-700 font-medium">Total</span>
                  <span className="text-blue-600 font-semibold">${getSelectedProductsTotal().toFixed(2)}</span>
                </div>
              )}
              {isPendingReview && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-amber-700">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs">Pending confirmation from {business?.name || 'business'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Quote Deposit Payment */}
          {quoteData?.id && !quoteData?.deposit_paid && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6 text-left" data-testid="quote-deposit-section">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-indigo-900">Secure your spot with a deposit</h3>
                  <p className="text-xs text-indigo-700 mt-1">
                    Pay a {quoteData?.deposit_percent || 25}% deposit now to lock in your appointment. Card details are processed securely by Stripe.
                  </p>
                </div>
              </div>
              <button
                type="button"
                data-testid="pay-deposit-btn"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/public/payments/checkout/quote`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ quote_id: quoteData.id, origin_url: window.location.origin })
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      throw new Error(err.detail || 'Failed to start checkout');
                    }
                    const data = await res.json();
                    if (data.url) window.location.href = data.url;
                  } catch (e) {
                    alert(e.message);
                  }
                }}
                className="w-full bg-indigo-600 text-white py-2.5 px-6 rounded-lg hover:bg-indigo-700 font-medium transition-colors text-sm"
              >
                Pay Deposit with Stripe
              </button>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-3">
            {clientLoggedIn ? (
              <button
                onClick={() => {
                  sessionStorage.removeItem('clientPortalBooking');
                  sessionStorage.removeItem('rescheduleAppointment');
                  window.location.href = '/client-portal';
                }}
                data-testid="go-to-account-btn"
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                Go to My Account
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setAuthMode('register');
                    setAuthForm({ ...authForm, email: formData.client_email, name: formData.client_name, phone: formData.client_phone });
                    setShowAuthModal(true);
                  }}
                  data-testid="create-account-btn"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  Create Account to Track Bookings
                </button>
                <button
                  onClick={() => {
                    setAuthMode('login');
                    setAuthForm({ ...authForm, email: formData.client_email });
                    setShowAuthModal(true);
                  }}
                  data-testid="login-btn"
                  className="w-full bg-white text-gray-700 py-3 px-6 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors"
                >
                  Already have an account? Sign In
                </button>
              </>
            )}
          </div>
          
          {fromClientPortal && (
            <button
              onClick={handleReturnToPortal}
              data-testid="return-to-portal-btn"
              className="w-full mt-3 text-gray-500 py-2 px-6 rounded-lg hover:text-gray-700 font-medium transition-colors"
            >
              ← Return to Client Portal
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`border-b ${isReschedule ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center gap-3">
            {business?.logo_url ? (
              <img src={business.logo_url} alt={business.name} className="h-10 w-10 rounded-lg object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">{business?.name?.charAt(0) || 'B'}</span>
              </div>
            )}
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{business?.name}</h1>
              <p className={`text-sm ${isReschedule ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                {isReschedule ? 'Reschedule Appointment' : 'Schedule Your Appointment'}
              </p>
            </div>
          </div>
          {isReschedule && (
            <div className="mt-3 p-3 bg-amber-100 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Rescheduling:</span> {formData.property_address}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Select a new date and time below. Services and property details cannot be changed.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1 max-w-4xl">
        {/* Progress Steps - Simplified for reschedule mode */}
        {isReschedule ? (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-800">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-medium">Select New Date & Time</span>
            </div>
          </div>
        ) : (
        <div className="flex items-center justify-between mb-8 overflow-x-auto">
          {[
            { num: 1, label: 'Services' },
            { num: 2, label: 'Details' },
            { num: 3, label: 'Add-ons' },
            { num: 4, label: 'Schedule' },
            { num: 5, label: 'Questions' }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <button
                onClick={() => s.num < step && setStep(s.num)}
                disabled={s.num > step}
                className={`flex items-center gap-2 ${s.num < step ? 'cursor-pointer' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step === s.num ? 'bg-blue-600 text-white' : step > s.num ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s.num ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : s.num}
                </div>
                <span className={`text-sm hidden sm:inline ${step >= s.num ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </button>
              {idx < 4 && <div className={`w-8 sm:w-16 h-0.5 mx-1 ${step > s.num ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        )}

        {/* Step 1: Services Selection (with SqFt) - Skip for reschedule mode */}
        {step === 1 && !isReschedule && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Select Services</h2>
            <p className="text-sm text-gray-500 mb-6">Enter square footage to see pricing</p>
            
            {/* Square Footage Input */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Property Square Footage <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.property_sqft}
                onChange={(e) => setFormData({ ...formData, property_sqft: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                placeholder="e.g., 2500"
              />
              {formData.property_sqft && (
                <p className="text-xs text-blue-600 mt-1">Prices calculated for {formData.property_sqft} sq ft</p>
              )}
            </div>
            
            {/* Show products only after sqft is entered */}
            {!formData.property_sqft && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <svg className="w-10 h-10 text-blue-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-blue-800 font-medium">Enter the property square footage above</p>
                <p className="text-blue-600 text-sm mt-1">This helps us calculate accurate pricing for your services</p>
              </div>
            )}

            {formData.property_sqft && (
              <>
            {/* Packages Section */}
            {packages.length > 0 && (
              <div className="mb-8">
                <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Packages
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {packages.map((pkg) => {
                    const isSelected = formData.selected_package === pkg.id;
                    const pkgPrice = getPackagePrice(pkg);
                    const pkgDuration = getPackageDuration(pkg);
                    const hasDiscount = pkg.discount_percentage > 0;
                    
                    return (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => selectPackage(pkg.id)}
                        className={`relative flex flex-col rounded-xl border-2 overflow-hidden text-left transition-all ${
                          isSelected ? 'border-purple-500 ring-2 ring-purple-100' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {pkg.image_url && (
                          <div className="h-32 bg-gray-100">
                            <img src={`${API_URL}${pkg.image_url}`} alt={pkg.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        <div className="p-4 flex-1 flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Package</span>
                            {hasDiscount && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                {pkg.discount_percentage}% OFF
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{pkg.name}</h3>
                          {pkg.description && (
                            <p className="text-xs text-gray-500 mb-2 line-clamp-2">{pkg.description}</p>
                          )}
                          {pkg.products && pkg.products.length > 0 && (
                            <p className="text-xs text-gray-400 mb-2">
                              Includes: {pkg.products.map(p => p.name).join(', ')}
                            </p>
                          )}
                          <div className="mt-auto flex items-end justify-between">
                            <span className="text-xl font-bold text-gray-900">${pkgPrice.toFixed(0)}</span>
                            {pkgDuration > 0 && (
                              <span className="text-xs text-gray-400">{pkgDuration} min</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Individual Products */}
            {products.length > 0 && (
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Individual Services
                </h3>
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4`}>
                  {products.map((product) => {
                    const isSelected = formData.selected_products.includes(product.id);
                    const price = getProductPrice(product);
                    const duration = getProductDuration(product);
                    const hasTiers = (product.pricing_tiers || []).length > 0;
                    const hasImage = product.image_url;
                    
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => toggleProduct(product.id)}
                        className={`relative flex flex-col rounded-xl border-2 overflow-hidden text-left transition-all ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {hasImage && (
                          <div className="h-32 bg-gray-100">
                            <img src={`${API_URL}${product.image_url}`} alt={product.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        <div className="p-4 flex-1 flex flex-col">
                          <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                          {product.description && (
                            <p className="text-xs text-gray-500 mb-3 line-clamp-2">{product.description}</p>
                          )}
                          <div className="mt-auto flex items-end justify-between">
                            <div>
                              <span className="text-xl font-bold text-gray-900">${price}</span>
                              {hasTiers && <span className="text-xs text-gray-400 ml-1">*</span>}
                            </div>
                            {duration > 0 && (
                              <span className="text-xs text-gray-400">{duration} min</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {products.length === 0 && packages.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No services available at this time.</p>
              </div>
            )}

            {(formData.selected_products.length > 0 || formData.selected_package) && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-blue-900">
                      {formData.selected_package ? '1 package' : `${formData.selected_products.length} service${formData.selected_products.length !== 1 ? 's' : ''}`} selected
                    </span>
                    {getTotalDuration() > 0 && (
                      <span className="text-sm text-blue-700 ml-2">• {getTotalDuration()} min total</span>
                    )}
                  </div>
                  <span className="text-2xl font-bold text-blue-600">${getSelectedProductsTotal().toFixed(2)}</span>
                </div>
              </div>
            )}
              </>
            )}

            {/* Mobile Continue Button - hidden on large screens */}
            <div className="mt-6 flex items-center justify-end lg:hidden">
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2()}
                className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Continue to Details
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Property & Contact Details */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Property & Contact Information</h2>
            
            {/* Client Login Status - Optional */}
            {clientLoggedIn ? (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium">Signed in as {formData.client_email}</span>
                  </div>
                  <button
                    onClick={() => {
                      localStorage.removeItem('clientToken');
                      setClientLoggedIn(false);
                      setClientToken(null);
                      setFromClientPortal(false);
                      setFormData(prev => ({
                        ...prev,
                        client_name: '',
                        client_email: '',
                        client_phone: '',
                      }));
                    }}
                    className="text-sm text-green-600 hover:text-green-800 font-medium"
                  >
                    Sign out / Switch account
                  </button>
                </div>
                <p className="text-xs text-green-600 mt-2">Your details have been auto-filled. You can edit them below if needed.</p>
              </div>
            ) : (
              <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-gray-600">Have an account? <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} className="text-blue-600 hover:text-blue-700 font-medium">Sign in</button> to auto-fill your info.</p>
                <button
                  onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap ml-3"
                >
                  Create Account
                </button>
              </div>
            )}
            
            <div className="space-y-5">
              <div ref={addressInputRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.property_address}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                  placeholder="Start typing an address..."
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => selectAddressSuggestion(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-sm text-gray-700">{suggestion.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
                  <input
                    type="text"
                    value={formData.client_company}
                    onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400"
                    placeholder="Real Estate Co."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Special Instructions</label>
                <textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 resize-none"
                  rows="3"
                  placeholder="Gate codes, parking info, special requests..."
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button onClick={() => setStep(1)} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900">
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedToStep3()}
                className="lg:hidden px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Continue to Add-ons
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Add-on Products */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Add-on Services</h2>
            <p className="text-sm text-gray-500 mb-6">Optional add-ons based on your selected services</p>
            
            {(() => {
              // Filter addons based on selected products
              const selectedProductIds = formData.selected_package 
                ? packages.find(p => p.id === formData.selected_package)?.product_ids || []
                : formData.selected_products;
              
              const availableAddons = addonProducts.filter(addon => 
                addon.is_active && 
                (addon.linked_product_ids?.length === 0 || 
                 addon.linked_product_ids?.some(pid => selectedProductIds.includes(pid)))
              );
              
              if (availableAddons.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p>No add-ons available for your selected services</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-3">
                  {availableAddons.map(addon => (
                    <label
                      key={addon.id}
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                        formData.selected_addons?.includes(addon.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={formData.selected_addons?.includes(addon.id) || false}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, selected_addons: [...(formData.selected_addons || []), addon.id] });
                            } else {
                              setFormData({ ...formData, selected_addons: (formData.selected_addons || []).filter(id => id !== addon.id) });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{addon.name}</p>
                          {addon.description && <p className="text-sm text-gray-500">{addon.description}</p>}
                          {addon.duration_minutes > 0 && (
                            <p className="text-xs text-gray-400 mt-1">+{addon.duration_minutes} min</p>
                          )}
                        </div>
                      </div>
                      <span className="text-lg font-semibold text-gray-900">+${addon.base_price?.toFixed(2) || '0.00'}</span>
                    </label>
                  ))}
                </div>
              );
            })()}
            
            {/* Add-ons Total */}
            {formData.selected_addons?.length > 0 && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-purple-700">Add-ons Total</span>
                  <span className="font-bold text-purple-700">
                    +${addonProducts
                      .filter(a => formData.selected_addons?.includes(a.id))
                      .reduce((sum, a) => sum + (a.base_price || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <button onClick={() => setStep(2)} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900">
                ← Back
              </button>
              <button
                onClick={() => setStep(4)}
                className="lg:hidden px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue to Schedule
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Schedule with Calendar and Photographer Selection */}
        {step === 4 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {/* Quote pre-fill banner */}
            {quoteData && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="quote-prefill-banner">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-blue-900">Your quote details have been loaded</p>
                    <p className="text-xs text-blue-700 mt-1">
                      {formData.property_address} &middot; {products.filter(p => formData.selected_products.includes(p.id)).map(p => p.name).join(', ')}
                    </p>
                    {formData.scheduled_date && formData.scheduled_time && (
                      <p className="text-xs text-blue-600 mt-1">
                        Pre-selected: {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at {formatTime(formData.scheduled_time)}
                        {' '}&mdash; confirm or choose a different time below
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Choose Date & Time</h2>
            
            {/* Photographer Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Select Photographer</label>
              {photographers.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 font-medium">No photographers available in your area</p>
                  <p className="text-amber-600 text-sm mt-1">
                    Unfortunately, none of our photographers service the location you entered. 
                    Please contact us directly to arrange service at this address.
                  </p>
                </div>
              ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, photographer_id: '', scheduled_time: '' });
                    setMileageFee(0);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    !formData.photographer_id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Any Photographer
                </button>
                {photographers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, photographer_id: p.id, scheduled_time: '' });
                      setMileageFee(p.mileage_fee || 0);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex flex-col items-start ${
                      formData.photographer_id === p.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {p.avatar_url ? (
                        <img src={`${API_URL}${p.avatar_url}`} alt={p.name} className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-600">
                          {p.name?.charAt(0)}
                        </div>
                      )}
                      {p.name}
                    </div>
                    {p.distance_miles !== null && p.distance_miles !== undefined && (
                      <div className={`text-xs mt-1 ${formData.photographer_id === p.id ? 'text-blue-200' : 'text-gray-500'}`}>
                        📍 {p.distance_miles} mi away
                        {p.mileage_fee > 0 && (
                          <span className={`ml-1 ${formData.photographer_id === p.id ? 'text-yellow-200' : 'text-amber-600'}`}>
                            (+${p.mileage_fee.toFixed(2)} travel)
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Date</label>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <span className="font-semibold text-gray-900">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">{day}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Time</label>
                {!formData.scheduled_date ? (
                  <div className="border border-gray-200 rounded-lg p-8 text-center h-[280px] flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-500">Select a date to see available times</p>
                  </div>
                ) : loadingSlots ? (
                  <div className="border border-gray-200 rounded-lg p-8 text-center h-[280px] flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading available times...</p>
                    <p className="text-xs text-gray-400 mt-1">Checking calendar for {getTotalDuration()} min appointment</p>
                  </div>
                ) : availability.calendar_error ? (
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-8 text-center h-[280px] flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-amber-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-amber-700 font-medium">Calendar Sync Unavailable</p>
                    <p className="text-sm text-amber-600 mt-1">{availability.message || 'Please contact the business to schedule.'}</p>
                  </div>
                ) : availability.slots.length === 0 ? (
                  <div className="border border-gray-200 rounded-lg p-8 text-center h-[280px] flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-500">No available times for this date.</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {availability.reason || 'Please try a different date.'}
                    </p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4 h-[280px] overflow-y-auto">
                    <div className="grid grid-cols-3 gap-2">
                      {availability.slots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setFormData({ ...formData, scheduled_time: time })}
                          className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                            formData.scheduled_time === time ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {formatTime(time)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary - Simplified for reschedule mode */}
            <div className={`mt-8 p-4 rounded-lg ${isReschedule ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isReschedule ? 'text-amber-900' : 'text-gray-900'}`}>
                {isReschedule ? 'Appointment Summary' : 'Booking Summary'}
              </h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between items-start">
                  <span className={isReschedule ? 'text-amber-700' : 'text-gray-500'}>Property</span>
                  <span className={`font-medium text-right max-w-[200px] truncate ${isReschedule ? 'text-amber-900' : 'text-gray-900'}`}>{formData.property_address}</span>
                </div>
                {!isReschedule && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Services</span>
                      <span className="text-gray-900 text-right">{getSelectedServiceNames() || '-'}</span>
                    </div>
                    {formData.selected_addons.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Add-ons</span>
                        <span className="text-gray-900">
                          {addonProducts.filter(a => formData.selected_addons.includes(a.id)).map(a => a.name).join(', ')}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {formData.photographer_id && (
                  <div className="flex justify-between">
                    <span className={isReschedule ? 'text-amber-700' : 'text-gray-500'}>Photographer</span>
                    <span className={isReschedule ? 'text-amber-900' : 'text-gray-900'}>{photographers.find(p => p.id === formData.photographer_id)?.name || 'Any'}</span>
                  </div>
                )}
                {formData.scheduled_date && formData.scheduled_time && (
                  <div className="flex justify-between">
                    <span className={isReschedule ? 'text-amber-700' : 'text-gray-500'}>
                      {isReschedule ? 'New Date & Time' : 'Date & Time'}
                    </span>
                    <span className={`font-medium ${isReschedule ? 'text-amber-900' : 'text-gray-900'}`}>
                      {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {formatTime(formData.scheduled_time)}
                    </span>
                  </div>
                )}
                {!isReschedule && (
                  <div className="pt-3 border-t border-gray-200 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Services Subtotal</span>
                      <span className="text-gray-900">${getSelectedProductsTotal().toFixed(2)}</span>
                    </div>
                    {getAddonsTotal() > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Add-ons</span>
                        <span className="text-gray-900">${getAddonsTotal().toFixed(2)}</span>
                      </div>
                    )}
                    {mileageFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Travel Fee</span>
                        <span className="text-amber-600">${mileageFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-gray-100">
                      <span className="text-gray-900 font-semibold">Total</span>
                      <span className="text-xl font-semibold text-blue-600">${getGrandTotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg">{error}</div>
            )}

            <div className="mt-6 flex items-center justify-between">
              {isReschedule ? (
                <button 
                  onClick={() => {
                    sessionStorage.removeItem('rescheduleAppointment');
                    window.location.href = '/';
                  }} 
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  ← Cancel
                </button>
              ) : (
                <button onClick={() => setStep(3)} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900">
                  ← Back
                </button>
              )}
              <button
                onClick={() => {
                  // In reschedule mode, just submit directly
                  if (isReschedule) {
                    handleSubmit();
                    return;
                  }
                  
                  // Check if there are any relevant order forms for selected products
                  const selectedProductIds = formData.selected_package 
                    ? packages.find(p => p.id === formData.selected_package)?.product_ids || []
                    : formData.selected_products;
                  
                  const relevantForms = orderForms.filter(form => 
                    form.is_active && 
                    form.questions?.length > 0 &&
                    form.product_ids?.some(pid => selectedProductIds.includes(pid))
                  );
                  
                  if (relevantForms.length > 0) {
                    setStep(5);
                  } else {
                    handleSubmit();
                  }
                }}
                disabled={!formData.scheduled_date || !formData.scheduled_time || loading}
                data-testid="reschedule-confirm-btn"
                className={`lg:hidden px-6 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed ${
                  isReschedule 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Processing...' : isReschedule ? 'Confirm Reschedule' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Custom Order Form Questions */}
        {step === 5 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Additional Information</h2>
            <p className="text-sm text-gray-500 mb-6">Please answer the following questions to complete your booking</p>
            
            {(() => {
              const selectedProductIds = formData.selected_package 
                ? packages.find(p => p.id === formData.selected_package)?.product_ids || []
                : formData.selected_products;
              
              const relevantForms = orderForms.filter(form => 
                form.is_active && 
                form.questions?.length > 0 &&
                form.product_ids?.some(pid => selectedProductIds.includes(pid))
              );
              
              return (
                <div className="space-y-6">
                  {relevantForms.map(form => (
                    <div key={form.id} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-4">{form.name}</h3>
                      <div className="space-y-4">
                        {form.questions.map(q => (
                          <div key={q.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              {q.label} {q.required && <span className="text-red-500">*</span>}
                            </label>
                            
                            {q.type === 'text' && (
                              <input
                                type="text"
                                placeholder={q.placeholder}
                                value={formData.custom_form_answers?.[q.id] || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            
                            {q.type === 'textarea' && (
                              <textarea
                                placeholder={q.placeholder}
                                rows={3}
                                value={formData.custom_form_answers?.[q.id] || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            
                            {q.type === 'number' && (
                              <input
                                type="number"
                                placeholder={q.placeholder}
                                value={formData.custom_form_answers?.[q.id] || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            
                            {q.type === 'date' && (
                              <input
                                type="date"
                                value={formData.custom_form_answers?.[q.id] || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            
                            {q.type === 'file' && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                  <label className="flex-1 cursor-pointer">
                                    <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${formData.custom_form_answers?.[q.id] ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400'}`}>
                                      {formData.custom_form_answers?.[q.id] ? (
                                        <div className="flex items-center justify-center gap-2 text-green-700">
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          <span className="text-sm font-medium">{formData.custom_form_answers[q.id].name}</span>
                                        </div>
                                      ) : (
                                        <div className="text-gray-500">
                                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                          </svg>
                                          <span className="text-sm">Click to upload file</span>
                                        </div>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          setFormData({
                                            ...formData,
                                            custom_form_answers: { ...formData.custom_form_answers, [q.id]: file }
                                          });
                                        }
                                      }}
                                    />
                                  </label>
                                  {formData.custom_form_answers?.[q.id] && (
                                    <button
                                      type="button"
                                      onClick={() => setFormData({
                                        ...formData,
                                        custom_form_answers: { ...formData.custom_form_answers, [q.id]: null }
                                      })}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {q.type === 'select' && (
                              <select
                                value={formData.custom_form_answers?.[q.id] || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select an option</option>
                                {q.options?.map((opt, i) => (
                                  <option key={i} value={opt}>{opt}</option>
                                ))}
                              </select>
                            )}
                            
                            {q.type === 'radio' && (
                              <div className="space-y-2">
                                {q.options?.map((opt, i) => (
                                  <label key={i} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={q.id}
                                      value={opt}
                                      checked={formData.custom_form_answers?.[q.id] === opt}
                                      onChange={(e) => setFormData({
                                        ...formData,
                                        custom_form_answers: { ...formData.custom_form_answers, [q.id]: e.target.value }
                                      })}
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm text-gray-700">{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            
                            {q.type === 'checkbox' && (
                              <div className="space-y-2">
                                {q.options?.map((opt, i) => (
                                  <label key={i} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={(formData.custom_form_answers?.[q.id] || []).includes(opt)}
                                      onChange={(e) => {
                                        const current = formData.custom_form_answers?.[q.id] || [];
                                        const updated = e.target.checked 
                                          ? [...current, opt]
                                          : current.filter(v => v !== opt);
                                        setFormData({
                                          ...formData,
                                          custom_form_answers: { ...formData.custom_form_answers, [q.id]: updated }
                                        });
                                      }}
                                      className="w-4 h-4 text-blue-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700">{opt}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Booking Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Booking Summary</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between items-start">
                  <span className="text-gray-500">Property</span>
                  <span className="text-gray-900 font-medium text-right max-w-[200px] truncate">{formData.property_address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Services</span>
                  <span className="text-gray-900 text-right">{getSelectedServiceNames() || '-'}</span>
                </div>
                {formData.selected_addons.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Add-ons</span>
                    <span className="text-gray-900 text-right">
                      {addonProducts.filter(a => formData.selected_addons.includes(a.id)).map(a => a.name).join(', ')}
                    </span>
                  </div>
                )}
                {formData.photographer_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Photographer</span>
                    <span className="text-gray-900">{photographers.find(p => p.id === formData.photographer_id)?.name || 'Any'}</span>
                  </div>
                )}
                {formData.scheduled_date && formData.scheduled_time && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date & Time</span>
                    <span className="text-gray-900">
                      {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {formatTime(formData.scheduled_time)}
                    </span>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Services Subtotal</span>
                    <span className="text-gray-900">${getSelectedProductsTotal().toFixed(2)}</span>
                  </div>
                  {getAddonsTotal() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Add-ons</span>
                      <span className="text-gray-900">${getAddonsTotal().toFixed(2)}</span>
                    </div>
                  )}
                  {mileageFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Travel Fee</span>
                      <span className="text-amber-600">${mileageFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="text-gray-900 font-semibold">Total</span>
                    <span className="text-xl font-semibold text-blue-600">${getGrandTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg">{error}</div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <button onClick={() => setStep(4)} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900">
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="lg:hidden px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Confirm Booking
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      
          {/* Order Summary Sidebar */}
          {!isReschedule && step > 0 && (
            <div className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Order Summary</h3>
                </div>
                <div className="p-4 space-y-4">
                  {/* Property */}
                  {formData.property_address && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Property</p>
                      <p className="text-sm text-gray-900 truncate">{formData.property_address}</p>
                      {formData.property_sqft && <p className="text-xs text-gray-500">{formData.property_sqft} sq ft</p>}
                    </div>
                  )}
                  
                  {/* Selected Services */}
                  {(formData.selected_package || formData.selected_products.length > 0) && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Services</p>
                      {formData.selected_package ? (
                        <div className="flex items-center justify-between text-sm animate-[slideIn_0.3s_ease-out]">
                          <span className="text-gray-900">{packages.find(p => p.id === formData.selected_package)?.name || 'Package'}</span>
                          <span className="font-medium text-gray-900">${getPackagePrice(packages.find(p => p.id === formData.selected_package))?.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {formData.selected_products.map((pid, idx) => {
                            const prod = products.find(p => p.id === pid);
                            if (!prod) return null;
                            return (
                              <div key={pid} className="flex items-center justify-between text-sm animate-[slideIn_0.3s_ease-out]" style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'both' }}>
                                <span className="text-gray-700 truncate flex-1 mr-2">{prod.name}</span>
                                <span className="text-gray-900 font-medium">${getProductPrice(prod)?.toFixed(2)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Add-ons */}
                  {formData.selected_addons.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Add-ons</p>
                      <div className="space-y-1">
                        {formData.selected_addons.map((aid, idx) => {
                          const addon = addonProducts.find(a => a.id === aid);
                          if (!addon) return null;
                          return (
                            <div key={aid} className="flex items-center justify-between text-sm animate-[slideIn_0.3s_ease-out]" style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'both' }}>
                              <span className="text-gray-700 truncate flex-1 mr-2">{addon.name}</span>
                              <span className="text-gray-900 font-medium">${addon.base_price?.toFixed(2) || '0.00'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Schedule */}
                  {formData.scheduled_date && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Scheduled</p>
                      <p className="text-sm text-gray-900">
                        {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {formData.scheduled_time && ` at ${formData.scheduled_time}`}
                      </p>
                    </div>
                  )}
                  
                  {/* Mileage Fee */}
                  {mileageFee > 0 && (
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Travel Fee</span>
                      <span>${mileageFee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                {/* Total */}
                <div className="bg-blue-50 px-4 py-3 border-t border-blue-100">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-blue-600">${calculateTotal().toFixed(2)}</span>
                  </div>
                  {getTotalDuration() > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Est. duration: {getTotalDuration() >= 60 ? `${Math.floor(getTotalDuration() / 60)}h${getTotalDuration() % 60 > 0 ? ` ${getTotalDuration() % 60}m` : ''}` : `${getTotalDuration()}m`}
                    </p>
                  )}
                </div>

                {/* Available Add-ons - Separate prominent section below summary */}
                {(() => {
                  const selectedProductIds = formData.selected_package 
                    ? packages.find(p => p.id === formData.selected_package)?.product_ids || []
                    : formData.selected_products;
                  
                  if (selectedProductIds.length === 0) return null;
                  
                  const seen = new Set();
                  const relevantAddons = addonProducts.filter(addon => {
                    if (!addon.is_active) return false;
                    if (seen.has(addon.id)) return false;
                    const linked = addon.linked_product_ids?.length === 0 || 
                      addon.linked_product_ids?.some(pid => selectedProductIds.includes(pid));
                    if (linked) seen.add(addon.id);
                    return linked;
                  });
                  
                  if (relevantAddons.length === 0) return null;
                  
                  return (
                    <div className="mt-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 overflow-hidden shadow-sm">
                      <div className="bg-purple-600 px-4 py-2.5 flex items-center gap-2">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <h4 className="text-sm font-semibold text-white">Enhance Your Shoot</h4>
                      </div>
                      <div className="p-3 space-y-2">
                        {relevantAddons.map(addon => {
                          const isAddonSelected = formData.selected_addons?.includes(addon.id);
                          return (
                            <label
                              key={addon.id}
                              className={`group flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                isAddonSelected 
                                  ? 'border-purple-500 bg-white shadow-md scale-[1.02]' 
                                  : 'border-transparent bg-white/70 hover:bg-white hover:border-purple-200 hover:shadow-sm'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isAddonSelected || false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({ ...formData, selected_addons: [...(formData.selected_addons || []), addon.id] });
                                  } else {
                                    setFormData({ ...formData, selected_addons: (formData.selected_addons || []).filter(id => id !== addon.id) });
                                  }
                                }}
                                className="w-4 h-4 text-purple-600 rounded border-gray-300 mt-0.5 transition-transform group-hover:scale-110"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-gray-900">{addon.name}</p>
                                  <span className={`text-sm font-bold ml-2 ${isAddonSelected ? 'text-purple-600' : 'text-gray-700'}`}>+${addon.base_price?.toFixed(2) || '0.00'}</span>
                                </div>
                                {addon.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{addon.description}</p>}
                                {addon.duration_minutes > 0 && (
                                  <p className="text-xs text-purple-400 mt-0.5">+{addon.duration_minutes} min</p>
                                )}
                              </div>
                              {isAddonSelected && (
                                <svg className="w-5 h-5 text-purple-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Continue/Submit Button */}
                <div className="p-4 border-t border-gray-100">
                  {step === 1 && (
                    <button
                      onClick={() => setStep(2)}
                      disabled={!canProceedToStep2()}
                      className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Continue to Details →
                    </button>
                  )}
                  {step === 2 && (
                    <button
                      onClick={() => setStep(3)}
                      disabled={!canProceedToStep3()}
                      className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Continue to Add-ons →
                    </button>
                  )}
                  {step === 3 && (
                    <button
                      onClick={() => setStep(4)}
                      className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Continue to Schedule →
                    </button>
                  )}
                  {step === 4 && (
                    <button
                      onClick={() => setStep(5)}
                      disabled={!formData.scheduled_date || !formData.scheduled_time}
                      className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Continue to Questions →
                    </button>
                  )}
                  {step === 5 && (
                    <button
                      onClick={handleSubmit}
                      disabled={loading || !canSubmit()}
                      className="w-full px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? 'Submitting...' : 'Submit Booking Request'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {authMode === 'login' ? 'Sign In to Your Account' : 'Create Your Account'}
              </h3>
              <button 
                onClick={() => setShowAuthModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg">
                {authError}
              </div>
            )}
            
            <div className="space-y-4">
              {authMode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={authForm.name}
                    onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={authForm.email}
                  onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={authMode === 'register' ? 'Create a password' : 'Enter your password'}
                />
              </div>
              
              {authMode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                  <input
                    type="tel"
                    value={authForm.phone}
                    onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              )}
              
              <button
                onClick={authMode === 'login' ? handleClientLogin : handleClientRegister}
                disabled={authLoading || !authForm.email || !authForm.password}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
              >
                {authLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {authMode === 'login' ? 'Signing In...' : 'Creating Account...'}
                  </>
                ) : (
                  authMode === 'login' ? 'Sign In' : 'Create Account'
                )}
              </button>
              
              <div className="text-center text-sm text-gray-500">
                {authMode === 'login' ? (
                  <>
                    Don&apos;t have an account?{' '}
                    <button 
                      onClick={() => { setAuthMode('register'); setAuthError(''); }}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Create one
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button 
                      onClick={() => { setAuthMode('login'); setAuthError(''); }}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicScheduling;
